/**
 * Main App Component
 * Sets up routing and layout
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { TechnicianLayout } from './components/TechnicianLayout';
import { TechnicianTodayPage } from './pages/TechnicianTodayPage';
import { EnrollmentPage } from './pages/enrollments/EnrollmentPage';
import { RenewalsPage } from './pages/enrollments/RenewalsPage';
import { ServiceBundlesPage } from './pages/settings/ServiceBundlesPage';
import { ServiceBundleEditorPage } from './pages/settings/ServiceBundleEditorPage';
import { BundleAlertsPage } from './pages/settings/BundleAlertsPage';
import { TechnicianJobPage } from './pages/TechnicianJobPage';
import { LoginPage } from './pages/LoginPage';
import { PortalLayout } from './pages/portal/PortalLayout';
import { PortalRegisterPage } from './pages/portal/PortalRegisterPage';
import { PortalVerifyPage, PortalAcceptInvitePage, PortalResetPasswordPage } from './pages/portal/PortalSetPasswordPage';
import { PortalForgotPasswordPage } from './pages/portal/PortalForgotPasswordPage';
import { PortalLoginPage } from './pages/portal/PortalLoginPage';
import { PortalDashboardPage } from './pages/portal/PortalDashboardPage';
import { PortalInvoicesPage } from './pages/portal/PortalInvoicesPage';
import { PortalInvoicePage } from './pages/portal/PortalInvoicePage';
import { PortalJobsPage } from './pages/portal/PortalJobsPage';
import { PortalJobPage } from './pages/portal/PortalJobPage';
import { PortalPaymentMethodsPage } from './pages/portal/PortalPaymentMethodsPage';
import { PortalStatementPage } from './pages/portal/PortalStatementPage';
import { PortalProfilePage } from './pages/portal/PortalProfilePage';
import { PortalRequestPage, PortalRequestServicePage, PortalRequestsPage } from './pages/portal/PortalRequestPages';
import { PortalProgramsPage } from './pages/portal/PortalProgramsPage';
import { RequestsPage } from './pages/requests/RequestsPage';
import { ServiceRequestPage } from './pages/requests/ServiceRequestPage';
import { CustomerPortalSettingsPage } from './pages/settings/CustomerPortalSettingsPage';
import { WebFormIntakeSettingsPage } from './pages/settings/WebFormIntakeSettingsPage';
import { GeocodingSettingsPage } from './pages/settings/GeocodingSettingsPage';
import { ImportExportPage } from './pages/settings/ImportExportPage';
import { PublicRequestServicePage } from './pages/public/PublicRequestServicePage';
import { DashboardPage } from './pages/DashboardPage';
import { SetupWizardPage } from './pages/SetupWizardPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { CustomerListPage } from './pages/CustomerListPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { CustomerFormPage } from './pages/CustomerFormPage';
import { PropertyFormPage } from './pages/PropertyFormPage';
import { PropertyDetailPage } from './pages/PropertyDetailPage';
import { PropertyRedirectPage } from './pages/PropertyRedirectPage';
import { SettingsLayout } from './components/SettingsLayout';
import { SettingsIndexPage } from './pages/settings/SettingsIndexPage';
import { TaxRatesPage } from './pages/settings/TaxRatesPage';
import { DiscountsPage } from './pages/settings/DiscountsPage';
import { ItemsPage } from './pages/settings/ItemsPage';
import { PriceChartsPage } from './pages/settings/PriceChartsPage';
import { LeadSourcesPage } from './pages/settings/LeadSourcesPage';
import { ChartOfAccountsPage } from './pages/settings/ChartOfAccountsPage';
import { UsersPage } from './pages/settings/UsersPage';
import { CompanySettingsPage } from './pages/settings/CompanySettingsPage';
import { AuditLogPage } from './pages/settings/AuditLogPage';
import { TechnicianSchedulesPage } from './pages/settings/TechnicianSchedulesPage';
import { RoutesPage } from './pages/settings/RoutesPage';
import { ChecklistTemplatesPage } from './pages/settings/ChecklistTemplatesPage';
import { StarterPacksPage } from './pages/settings/StarterPacksPage';
import { RecurringPresetsPage } from './pages/settings/RecurringPresetsPage';
import { DispatchPage } from './pages/DispatchPage';
import { WorkOrdersPage } from './pages/WorkOrdersPage';
import { DispatchPrintPage } from './pages/print/DispatchPrintPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { SecurityPage } from './pages/SecurityPage';
import { MfaPolicySettingsPage } from './pages/settings/MfaPolicySettingsPage';
import { StaffAlertPreferencesPage } from './pages/settings/StaffAlertPreferencesPage';
import { StaffNotificationsPage } from './pages/notifications/StaffNotificationsPage';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { TenantDetailPage } from './pages/admin/TenantDetailPage';
import { UserRole } from './api/types';
import { useAuth } from './context/AuthContext';
import { homePathFor } from './utils/permissions';
import { JobListPage } from './pages/JobListPage';
import { JobCreatePage } from './pages/JobCreatePage';
import { JobDetailPage } from './pages/JobDetailPage';
import { InvoiceListPage } from './pages/InvoiceListPage';
import { InvoiceCreatePage } from './pages/InvoiceCreatePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { FinanceChargesPage } from './pages/FinanceChargesPage';
import { FlatContractBillingPage } from './pages/FlatContractBillingPage';
import { AutoInvoicingPage } from './pages/AutoInvoicingPage';
import { DunningPage } from './pages/DunningPage';
import { DunningSettingsPage } from './pages/settings/DunningSettingsPage';
import { PaymentListPage } from './pages/PaymentListPage';
import { PaymentCreatePage } from './pages/PaymentCreatePage';
import { PaymentDetailPage } from './pages/PaymentDetailPage';
import { ReportsIndexPage } from './pages/reports/ReportsIndexPage';
import { TrialBalanceReportPage } from './pages/reports/TrialBalanceReportPage';
import { ARAgingReportPage } from './pages/reports/ARAgingReportPage';
import { RevenueReportPage } from './pages/reports/RevenueReportPage';
import { JobReportPage } from './pages/reports/JobReportPage';
import { PaymentReportPage } from './pages/reports/PaymentReportPage';
import { CustomerStatementPage } from './pages/reports/CustomerStatementPage';
import { InvoicePrintPage } from './pages/print/InvoicePrintPage';
import { JobPrintPage } from './pages/print/JobPrintPage';
import { StatementPrintPage } from './pages/print/StatementPrintPage';
import { RecurringListPage } from './pages/RecurringListPage';
import { RecurringCreatePage } from './pages/RecurringCreatePage';
import { RecurringDetailPage } from './pages/RecurringDetailPage';
import { LedgerListPage } from './pages/LedgerListPage';
import { LedgerDetailPage } from './pages/LedgerDetailPage';
import { LedgerEntryCreatePage } from './pages/LedgerEntryCreatePage';
import { InventoryPage } from './pages/InventoryPage';
import { NotificationSettingsPage } from './pages/settings/NotificationSettingsPage';
import { NotificationHistoryPage } from './pages/settings/NotificationHistoryPage';
import { NotificationTemplatePage } from './pages/settings/NotificationTemplatePage';
import { PublicInvoicePage, PublicStatementPage } from './pages/public/PublicDocumentPages';
import { PaymentsSettingsPage } from './pages/settings/PaymentsSettingsPage';
import { OnlinePaymentsPage } from './pages/OnlinePaymentsPage';
import { EstimateListPage } from './pages/estimates/EstimateListPage';
import { EstimateFormPage } from './pages/estimates/EstimateFormPage';
import { EstimateDetailPage } from './pages/estimates/EstimateDetailPage';
import { EstimatePrintPage } from './pages/print/EstimatePrintPage';
import { PortalEstimatePage, PortalEstimatesPage } from './pages/portal/PortalEstimatePages';
import { PublicEstimatePage } from './pages/public/PublicEstimatePage';
import { PublicSurveyPage } from './pages/public/PublicSurveyPage';
import { SurveysInboxPage } from './pages/surveys/SurveysInboxPage';
import { SurveySettingsPage } from './pages/settings/SurveySettingsPage';
import { CustomerSatisfactionReportPage } from './pages/reports/CustomerSatisfactionReportPage';
import { PortalRolloutListPage } from './pages/portalRollout/PortalRolloutListPage';
import { PortalRolloutNewPage } from './pages/portalRollout/PortalRolloutNewPage';
import { PortalRolloutDetailPage } from './pages/portalRollout/PortalRolloutDetailPage';
import { InboxPage } from './pages/inbox/InboxPage';
import { PortalMessagesPage } from './pages/portal/PortalMessagesPage';

/** Must match canUsePortal in utils/permissions.ts (backend /api/portal/* is CUSTOMER_PORTAL only; tracker item 45). */
const portalRoles = [UserRole.CUSTOMER_PORTAL];
const jobReaderRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.TECHNICIAN];
/** Must match canUseTechnicianView in utils/permissions.ts (backend technician module is staff-only; tracker item 42). */
const technicianViewRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.TECHNICIAN];
const jobOfficeRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canReadCustomers in utils/permissions.ts (backend customer/property reads deny CUSTOMER_PORTAL). */
const customerReaderRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.TECHNICIAN];
/** Must match canViewReports in utils/permissions.ts. */
const reportRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canViewRecurring / canManageRecurring in utils/permissions.ts. */
const recurringReaderRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.TECHNICIAN];
const recurringOfficeRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canViewLedger in utils/permissions.ts (backend /gl/journal-entries is office-only). */
const ledgerRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canViewAuditLog in utils/permissions.ts (backend /audit-logs is ADMIN-only). */
const auditLogRoles = [UserRole.ADMIN];
/** Must match canManageInventory in utils/permissions.ts (backend receive/adjust/valuation are office-only). */
const inventoryRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canManageNotifications / canSendNotifications in utils/permissions.ts (tracker item 35). */
const notificationAdminRoles = [UserRole.ADMIN];
const notificationOfficeRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canViewDispatch / canManageDispatch in utils/permissions.ts (backend /dispatch is office-only; tracker item 40). */
const dispatchRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
// Checklist templates (tracker item 44): backend writes are ADMIN / OFFICE_MANAGER; see canManageChecklistTemplates
const checklistTemplateRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canPrintWorkOrders in utils/permissions.ts (backend /work-order-batches is office-only; tracker item 58). */
const workOrderRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canManageSetup in utils/permissions.ts (backend /setup is ADMIN-only; tracker item 75). */
const setupRoles = [UserRole.ADMIN];
/** Must match canManageStarterTemplates in utils/permissions.ts (backend /starter-templates is ADMIN-only; tracker item 76). */
const starterPackRoles = [UserRole.ADMIN];
/** Must match canViewRecurringPresets in utils/permissions.ts (staff read presets; writes are checked inside the page; tracker item 76). */
const recurringPresetViewRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.TECHNICIAN];
/** Must match canManageOnlinePayments / canUseOnlinePayments in utils/permissions.ts (tracker item 38). */
const onlinePaymentAdminRoles = [UserRole.ADMIN];
const onlinePaymentOfficeRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canManageRequests in utils/permissions.ts (backend /service-requests, /reschedule-requests and /portal-access-requests are office-only; tracker item 45). */
const requestRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canManagePortalSettings in utils/permissions.ts (the portal fields go through PUT /company-settings, ADMIN only). */
const portalSettingsRoles = [UserRole.ADMIN];
/** Must match canManageGeocoding in utils/permissions.ts (backend requireRole('ADMIN') on /geocoding). */
const geocodingSettingsRoles = [UserRole.ADMIN];
/** Two-step verification (tracker item 63): own page for every staff role, the policy for ADMIN */
const mfaRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.OFFICE, UserRole.TECHNICIAN];
const mfaPolicyRoles = [UserRole.ADMIN];
/** Must match canManageEstimates in utils/permissions.ts (backend /estimates is office-only; tracker item 39). */
const estimateRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canViewSurveys in utils/permissions.ts (backend /surveys and /reports/customer-satisfaction are ADMIN and OFFICE_MANAGER; tracker item 69). */
const surveyRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canManageBundles / canManageBundleAlerts in utils/permissions.ts (backend /service-bundles writes and /enrollments are ADMIN and OFFICE_MANAGER, /settings/bundle-alerts is ADMIN; tracker item 85). */
const bundleRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
const bundleAlertRoles = [UserRole.ADMIN];
/** Must match canManagePortalRollout in utils/permissions.ts (backend /portal-rollout is ADMIN and OFFICE_MANAGER; tracker item 81). */
const portalRolloutRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** Must match canExportData in utils/permissions.ts (tracker item 52; the import part is ADMIN only inside the page). */
const importExportRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
/** The inbox routes admit technicians too; the company's inboxAccess setting decides on the server (tracker item 56). */
/** Must match canViewStaffNotifications in utils/permissions.ts (backend /staff-notifications is ADMIN and OFFICE_MANAGER; tracker item 62). */
const staffNotificationRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER];
const inboxRoles = [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.TECHNICIAN];

/** The dashboard, except a TECHNICIAN lands on the mobile technician view. */
function HomeRoute() {
  const { user } = useAuth();
  const home = homePathFor(user);
  return home === '/' ? <DashboardPage /> : <Navigate to={home} replace />;
}

export function App() {
  return (
    <Router>
      <Routes>
        {/* System administrator console - own provider tree, kept entirely
            separate from the tenant AuthProvider below (no shared state). */}
        <Route
          element={
            <AdminAuthProvider>
              <Outlet />
            </AdminAuthProvider>
          }
        >
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminDashboardPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/tenants/:id"
            element={
              <AdminProtectedRoute>
                <TenantDetailPage />
              </AdminProtectedRoute>
            }
          />
        </Route>

        {/* Public read-only documents behind emailed links (tracker item 35):
            no auth, no app nav; the token is the only access. */}
        <Route path="/view/invoice/:token" element={<PublicInvoicePage />} />
        {/* The public web form (tracker item 49): no sign-in, identified by the company's form key */}
        <Route path="/request-service/:key" element={<PublicRequestServicePage />} />
        <Route path="/view/statement/:token" element={<PublicStatementPage />} />
        {/* Public estimate link: view, approve, decline (tracker item 39) */}
        <Route path="/view/estimate/:token" element={<PublicEstimatePage />} />
        {/* Public customer survey link: rate a finished visit (tracker item 69); no app chrome */}
        <Route path="/view/survey/:token" element={<PublicSurveyPage />} />

        {/* Customer portal public pages (tracker item 45): no session and no staff
            chrome; reachable signed in or out. The sign-in page (needs the session)
            is inside the AuthProvider below. */}
        <Route path="/portal/register" element={<PortalRegisterPage />} />
        <Route path="/portal/:slug/register" element={<PortalRegisterPage />} />
        <Route path="/portal/verify" element={<PortalVerifyPage />} />
        <Route path="/portal/accept-invite" element={<PortalAcceptInvitePage />} />
        <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
        <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />

        {/* Tenant application */}
        <Route
          element={
            <AuthProvider>
              <Outlet />
            </AuthProvider>
          }
        >
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          {/* Portal sign in; /portal/:slug is the company landing page of invoice emails
              (static /portal/* paths win over :slug, see PORTAL_RESERVED_SEGMENTS) */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal/:slug" element={<PortalLoginPage />} />

          {/* Self-service / forced password change. Authenticated, but outside
              AppLayout: while mustChangePassword is set, ProtectedRoute keeps
              the user here and every other route would 403 anyway. */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute allowPortalUsers>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />

          {/* Two-step verification (tracker item 63). Outside AppLayout: also the
              forced enrollment gate, where every other route would 403. */}
          <Route
            path="/account/security"
            element={
              <ProtectedRoute requiredRole={mfaRoles}>
                <SecurityPage />
              </ProtectedRoute>
            }
          />

          {/* Printable documents: authenticated and role-gated like their
              screens, but outside AppLayout so no nav reaches the paper. */}
          <Route path="/print/invoices/:id" element={<ProtectedRoute requiredRole={jobOfficeRoles}><InvoicePrintPage /></ProtectedRoute>} />
          <Route path="/print/estimates/:id" element={<ProtectedRoute requiredRole={estimateRoles}><EstimatePrintPage /></ProtectedRoute>} />
          <Route path="/print/jobs/:id" element={<ProtectedRoute requiredRole={jobReaderRoles}><JobPrintPage /></ProtectedRoute>} />
          <Route path="/print/dispatch" element={<ProtectedRoute requiredRole={dispatchRoles}><DispatchPrintPage /></ProtectedRoute>} />
          <Route path="/print/customers/:customerId/statement" element={<ProtectedRoute requiredRole={reportRoles}><StatementPrintPage /></ProtectedRoute>} />

          {/* Technician view (tracker item 42): mobile shell with a slim header,
              outside AppLayout so there is no desktop sidebar. */}
          <Route
            element={
              <ProtectedRoute requiredRole={technicianViewRoles}>
                <TechnicianLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/tech" element={<TechnicianTodayPage />} />
            <Route path="/tech/jobs/:id" element={<TechnicianJobPage />} />
          </Route>

          {/* Customer portal (tracker item 45): mobile shell outside AppLayout, CUSTOMER_PORTAL
              only (staff get Access Denied here, portal users never reach the staff routes
              below). The request pages (phase 4) are beside the others. */}
          <Route
            element={
              <ProtectedRoute requiredRole={portalRoles} loginPath="/portal/login">
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/portal" element={<PortalDashboardPage />} />
            <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
            <Route path="/portal/invoices/:id" element={<PortalInvoicePage />} />
            <Route path="/portal/jobs" element={<PortalJobsPage />} />
            <Route path="/portal/jobs/:id" element={<PortalJobPage />} />
            <Route path="/portal/payment-methods" element={<PortalPaymentMethodsPage />} />
            <Route path="/portal/statement" element={<PortalStatementPage />} />
            <Route path="/portal/profile" element={<PortalProfilePage />} />
            {/* Estimates to approve or decline (tracker item 39) */}
            <Route path="/portal/estimates" element={<PortalEstimatesPage />} />
            <Route path="/portal/estimates/:id" element={<PortalEstimatePage />} />
            {/* Requests (phase 4); also open to a login that is not linked yet (it may ask for a service) */}
            <Route path="/portal/request-service" element={<PortalRequestServicePage />} />
            <Route path="/portal/requests" element={<PortalRequestsPage />} />
            <Route path="/portal/requests/:id" element={<PortalRequestPage />} />
            <Route path="/portal/programs" element={<PortalProgramsPage />} />
            {/* Messages with the office (tracker item 56); the office's notice email links here */}
            <Route path="/portal/messages" element={<PortalMessagesPage />} />
          </Route>

          {/* Protected Routes - share the AppLayout nav/header chrome */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<HomeRoute />} />

            {/* Customers */}
            <Route path="/customers" element={<ProtectedRoute requiredRole={customerReaderRoles}><CustomerListPage /></ProtectedRoute>} />
            <Route path="/customers/new" element={<ProtectedRoute requiredRole={customerReaderRoles}><CustomerFormPage /></ProtectedRoute>} />
            <Route path="/customers/:id" element={<ProtectedRoute requiredRole={customerReaderRoles}><CustomerDetailPage /></ProtectedRoute>} />
            <Route path="/customers/:id/edit" element={<ProtectedRoute requiredRole={customerReaderRoles}><CustomerFormPage /></ProtectedRoute>} />
            <Route path="/customers/:customerId/properties/new" element={<ProtectedRoute requiredRole={customerReaderRoles}><PropertyFormPage /></ProtectedRoute>} />
            <Route path="/customers/:customerId/properties/:propertyId" element={<ProtectedRoute requiredRole={customerReaderRoles}><PropertyDetailPage /></ProtectedRoute>} />
            <Route path="/properties/:id" element={<ProtectedRoute requiredRole={customerReaderRoles}><PropertyRedirectPage /></ProtectedRoute>} />
            <Route path="/customers/:customerId/properties/:propertyId/edit" element={<ProtectedRoute requiredRole={customerReaderRoles}><PropertyFormPage /></ProtectedRoute>} />

            {/* Conversation inbox (tracker item 56): the inboxAccess setting decides for technicians (the page shows the server's 403) */}
            <Route path="/setup" element={<ProtectedRoute requiredRole={setupRoles}><SetupWizardPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute requiredRole={staffNotificationRoles}><StaffNotificationsPage /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute requiredRole={inboxRoles}><InboxPage /></ProtectedRoute>} />
            <Route path="/inbox/unmatched" element={<ProtectedRoute requiredRole={inboxRoles}><InboxPage tab="unmatched" /></ProtectedRoute>} />
            <Route path="/inbox/unmatched/:conversationId" element={<ProtectedRoute requiredRole={inboxRoles}><InboxPage tab="unmatched" /></ProtectedRoute>} />
            <Route path="/inbox/:conversationId" element={<ProtectedRoute requiredRole={inboxRoles}><InboxPage /></ProtectedRoute>} />

            {/* Settings (shared tab bar) */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<SettingsIndexPage />} />
              <Route path="company" element={<CompanySettingsPage />} />
              <Route path="tax-rates" element={<TaxRatesPage />} />
              <Route path="discounts" element={<DiscountsPage />} />
              <Route path="items" element={<ItemsPage />} />
              <Route path="price-charts" element={<PriceChartsPage />} />
              <Route path="lead-sources" element={<LeadSourcesPage />} />
              <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="audit-log" element={<ProtectedRoute requiredRole={auditLogRoles}><AuditLogPage /></ProtectedRoute>} />
              <Route path="import-export" element={<ProtectedRoute requiredRole={importExportRoles}><ImportExportPage /></ProtectedRoute>} />
              <Route path="notifications" element={<ProtectedRoute requiredRole={notificationAdminRoles}><NotificationSettingsPage /></ProtectedRoute>} />
              <Route path="notifications/history" element={<ProtectedRoute requiredRole={notificationOfficeRoles}><NotificationHistoryPage /></ProtectedRoute>} />
              <Route path="notifications/messages/:eventKey" element={<ProtectedRoute requiredRole={notificationAdminRoles}><NotificationTemplatePage /></ProtectedRoute>} />
              <Route path="payments" element={<ProtectedRoute requiredRole={onlinePaymentAdminRoles}><PaymentsSettingsPage /></ProtectedRoute>} />
              <Route path="dunning" element={<ProtectedRoute requiredRole={notificationAdminRoles}><DunningSettingsPage /></ProtectedRoute>} />
              <Route path="customer-portal" element={<ProtectedRoute requiredRole={portalSettingsRoles}><CustomerPortalSettingsPage /></ProtectedRoute>} />
              <Route path="web-form" element={<ProtectedRoute requiredRole={portalSettingsRoles}><WebFormIntakeSettingsPage /></ProtectedRoute>} />
              {/* Geocoding (tracker item 41): ADMIN, like canManageGeocoding */}
              <Route path="geocoding" element={<ProtectedRoute requiredRole={geocodingSettingsRoles}><GeocodingSettingsPage /></ProtectedRoute>} />
              {/* Portal rollout campaigns (tracker item 81): ADMIN and OFFICE_MANAGER, like canManagePortalRollout */}
              <Route path="portal-rollout" element={<ProtectedRoute requiredRole={portalRolloutRoles}><PortalRolloutListPage /></ProtectedRoute>} />
              <Route path="portal-rollout/new" element={<ProtectedRoute requiredRole={portalRolloutRoles}><PortalRolloutNewPage /></ProtectedRoute>} />
              <Route path="portal-rollout/:campaignId" element={<ProtectedRoute requiredRole={portalRolloutRoles}><PortalRolloutDetailPage /></ProtectedRoute>} />
              <Route path="surveys" element={<ProtectedRoute requiredRole={surveyRoles}><SurveySettingsPage /></ProtectedRoute>} />
              {/* Service bundles (tracker item 85): alerts before :id so "alerts" is not read as a bundle id (v6 ranks static segments first anyway) */}
              <Route path="service-bundles" element={<ProtectedRoute requiredRole={bundleRoles}><ServiceBundlesPage /></ProtectedRoute>} />
              <Route path="service-bundles/new" element={<ProtectedRoute requiredRole={bundleRoles}><ServiceBundleEditorPage /></ProtectedRoute>} />
              <Route path="service-bundles/alerts" element={<ProtectedRoute requiredRole={bundleAlertRoles}><BundleAlertsPage /></ProtectedRoute>} />
              <Route path="service-bundles/:id" element={<ProtectedRoute requiredRole={bundleRoles}><ServiceBundleEditorPage /></ProtectedRoute>} />
              <Route path="my-alerts" element={<ProtectedRoute requiredRole={staffNotificationRoles}><StaffAlertPreferencesPage /></ProtectedRoute>} />
              <Route path="security" element={<ProtectedRoute requiredRole={mfaPolicyRoles}><MfaPolicySettingsPage /></ProtectedRoute>} />
              <Route path="technician-schedules" element={<ProtectedRoute requiredRole={dispatchRoles}><TechnicianSchedulesPage /></ProtectedRoute>} />
              <Route path="routes" element={<ProtectedRoute requiredRole={dispatchRoles}><RoutesPage /></ProtectedRoute>} />
              <Route path="checklist-templates" element={<ProtectedRoute requiredRole={checklistTemplateRoles}><ChecklistTemplatesPage /></ProtectedRoute>} />
              {/* Industry starter packs and recurring presets (tracker item 76) */}
              <Route path="starter-packs" element={<ProtectedRoute requiredRole={starterPackRoles}><StarterPacksPage /></ProtectedRoute>} />
              <Route path="recurring-presets" element={<ProtectedRoute requiredRole={recurringPresetViewRoles}><RecurringPresetsPage /></ProtectedRoute>} />
            </Route>

            {/* Jobs */}
            <Route path="/jobs" element={<ProtectedRoute requiredRole={jobReaderRoles}><JobListPage /></ProtectedRoute>} />
            <Route path="/jobs/new" element={<ProtectedRoute requiredRole={jobOfficeRoles}><JobCreatePage /></ProtectedRoute>} />
            <Route path="/jobs/:id" element={<ProtectedRoute requiredRole={jobReaderRoles}><JobDetailPage /></ProtectedRoute>} />

            {/* Requests inbox: service, reschedule and portal access requests (office roles only; see canManageRequests) */}
            <Route path="/requests" element={<ProtectedRoute requiredRole={requestRoles}><RequestsPage /></ProtectedRoute>} />
            <Route path="/requests/service/:id" element={<ProtectedRoute requiredRole={requestRoles}><ServiceRequestPage /></ProtectedRoute>} />

            {/* Estimates / quotes (office roles only; see canManageEstimates). /estimates/new before /estimates/:id. */}
            <Route path="/estimates" element={<ProtectedRoute requiredRole={estimateRoles}><EstimateListPage /></ProtectedRoute>} />
            <Route path="/estimates/new" element={<ProtectedRoute requiredRole={estimateRoles}><EstimateFormPage /></ProtectedRoute>} />
            <Route path="/estimates/:id" element={<ProtectedRoute requiredRole={estimateRoles}><EstimateDetailPage /></ProtectedRoute>} />
            <Route path="/estimates/:id/edit" element={<ProtectedRoute requiredRole={estimateRoles}><EstimateFormPage /></ProtectedRoute>} />

            {/* Service bundle enrollments and the renewals list (office roles only; see canManageBundles; tracker item 85) */}
            <Route path="/enrollments/:id" element={<ProtectedRoute requiredRole={bundleRoles}><EnrollmentPage /></ProtectedRoute>} />
            <Route path="/renewals" element={<ProtectedRoute requiredRole={bundleRoles}><RenewalsPage /></ProtectedRoute>} />

            {/* Customer survey answers (office roles only; see canViewSurveys) */}
            <Route path="/surveys" element={<ProtectedRoute requiredRole={surveyRoles}><SurveysInboxPage /></ProtectedRoute>} />

            {/* Dispatch board (office roles only; see canViewDispatch) */}
            <Route path="/dispatch" element={<ProtectedRoute requiredRole={dispatchRoles}><DispatchPage /></ProtectedRoute>} />

            {/* Work Orders: bulk printing and confirmation (office roles only; see canPrintWorkOrders).
                Prints in place: AppLayout and the page controls are hidden on paper. */}
            <Route path="/work-orders" element={<ProtectedRoute requiredRole={workOrderRoles}><WorkOrdersPage /></ProtectedRoute>} />

            {/* Recurring schedules (TECHNICIAN reads, office roles write; see canViewRecurring / canManageRecurring) */}
            <Route path="/recurring" element={<ProtectedRoute requiredRole={recurringReaderRoles}><RecurringListPage /></ProtectedRoute>} />
            <Route path="/recurring/new" element={<ProtectedRoute requiredRole={recurringOfficeRoles}><RecurringCreatePage /></ProtectedRoute>} />
            <Route path="/recurring/:id" element={<ProtectedRoute requiredRole={recurringReaderRoles}><RecurringDetailPage /></ProtectedRoute>} />

            {/* Invoices */}
            <Route path="/invoices" element={<ProtectedRoute requiredRole={jobOfficeRoles}><InvoiceListPage /></ProtectedRoute>} />
            <Route path="/invoices/new" element={<ProtectedRoute requiredRole={jobOfficeRoles}><InvoiceCreatePage /></ProtectedRoute>} />
            {/* Before /invoices/:id so "finance-charges" is not read as an invoice id (v6 ranks static segments first anyway) */}
            <Route path="/invoices/finance-charges" element={<ProtectedRoute requiredRole={jobOfficeRoles}><FinanceChargesPage /></ProtectedRoute>} />
            <Route path="/invoices/flat-contracts" element={<ProtectedRoute requiredRole={jobOfficeRoles}><FlatContractBillingPage /></ProtectedRoute>} />
            <Route path="/invoices/auto-invoicing" element={<ProtectedRoute requiredRole={jobOfficeRoles}><AutoInvoicingPage /></ProtectedRoute>} />
            <Route path="/invoices/dunning" element={<ProtectedRoute requiredRole={jobOfficeRoles}><DunningPage /></ProtectedRoute>} />
            <Route path="/invoices/:id" element={<ProtectedRoute requiredRole={jobOfficeRoles}><InvoiceDetailPage /></ProtectedRoute>} />

            {/* Payments */}
            <Route path="/payments" element={<ProtectedRoute requiredRole={jobOfficeRoles}><PaymentListPage /></ProtectedRoute>} />
            <Route path="/payments/online" element={<ProtectedRoute requiredRole={onlinePaymentOfficeRoles}><OnlinePaymentsPage /></ProtectedRoute>} />
            <Route path="/payments/new" element={<ProtectedRoute requiredRole={jobOfficeRoles}><PaymentCreatePage /></ProtectedRoute>} />
            <Route path="/payments/:id" element={<ProtectedRoute requiredRole={jobOfficeRoles}><PaymentDetailPage /></ProtectedRoute>} />

            {/* Ledger (journal entries; office roles only, see canViewLedger). /ledger/new before /ledger/:id. */}
            <Route path="/ledger" element={<ProtectedRoute requiredRole={ledgerRoles}><LedgerListPage /></ProtectedRoute>} />
            <Route path="/ledger/new" element={<ProtectedRoute requiredRole={ledgerRoles}><LedgerEntryCreatePage /></ProtectedRoute>} />
            <Route path="/ledger/:id" element={<ProtectedRoute requiredRole={ledgerRoles}><LedgerDetailPage /></ProtectedRoute>} />

            {/* Inventory (office roles only, see canManageInventory) */}
            <Route path="/inventory" element={<ProtectedRoute requiredRole={inventoryRoles}><InventoryPage /></ProtectedRoute>} />

            {/* Reports (UI-gated to office roles; see canViewReports) */}
            <Route path="/reports" element={<ProtectedRoute requiredRole={reportRoles}><ReportsIndexPage /></ProtectedRoute>} />
            <Route path="/reports/trial-balance" element={<ProtectedRoute requiredRole={reportRoles}><TrialBalanceReportPage /></ProtectedRoute>} />
            <Route path="/reports/ar-aging" element={<ProtectedRoute requiredRole={reportRoles}><ARAgingReportPage /></ProtectedRoute>} />
            <Route path="/reports/revenue" element={<ProtectedRoute requiredRole={reportRoles}><RevenueReportPage /></ProtectedRoute>} />
            <Route path="/reports/jobs" element={<ProtectedRoute requiredRole={reportRoles}><JobReportPage /></ProtectedRoute>} />
            <Route path="/reports/payments" element={<ProtectedRoute requiredRole={reportRoles}><PaymentReportPage /></ProtectedRoute>} />
            <Route path="/reports/customer-satisfaction" element={<ProtectedRoute requiredRole={reportRoles}><CustomerSatisfactionReportPage /></ProtectedRoute>} />
            <Route path="/customers/:customerId/statement" element={<ProtectedRoute requiredRole={reportRoles}><CustomerStatementPage /></ProtectedRoute>} />
          </Route>

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
